import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Bell,
  Lock,
  Shield,
  LogOut,
  XCircle,
  Link2,
} from "lucide-react";
import { ListContainer, ListItem } from "@/ui/components/shared/ListItem";
import { UserProfilePicture } from "@/ui/components/UserProfilePicture";
import { useAppState, useActions } from "@/state/legacy_module_api";
import { useAppDialog } from "@/state/legacy_module_api";
import { appConfig } from "@/app/module_api";
import { getVersion, getGitHash, getBuildDate, disconnectWebSocket } from "@/core_bridge/module_api";

// Panel components
import ProfilePanel from "./settings-panels/ProfilePanel";
import NotificationsPanel from "./settings-panels/NotificationsPanel";
import LockCodesPanel from "./settings-panels/LockCodesPanel";
import BackupPanel from "./settings-panels/BackupPanel";

type SettingsPanel =
  | "none"
  | "profile"
  | "notifications"
  | "lock-codes"
  | "backup";

const SettingsNav = () => {
  const { myUsername, myHandle, myUserId, hilo, apiToken } = useAppState();
  const actions = useActions();
  const { confirm } = useAppDialog();
  const navigate = useNavigate();
  const myUser = useAppState().user.contactsById[myUserId];

  const [selectedPanel, setSelectedPanel] = useState<SettingsPanel>("none");
  const appVersion = import.meta.env.VITE_APP_VERSION || getVersion();

  const handleLogout = () => {
    confirm(
      "Are you sure you want to logout? You will need to link this device again to access your account.",
      () => {
        actions.logout();
      },
      "Logout"
    );
  };

  const renderPanel = () => {
    switch (selectedPanel) {
      case "profile":
        return <ProfilePanel />;
      case "notifications":
        return <NotificationsPanel />;
      case "lock-codes":
        return <LockCodesPanel />;
      case "backup":
        return <BackupPanel />;
      default:
        return (
          <div className="h-full flex items-center justify-center text-muted-foreground px-6">
            Select a setting to view details.
          </div>
        );
    }
  };

  return (
    <div data-name="settings-nav" className="h-full grid grid-cols-[minmax(320px,400px)_1fr] min-h-0">
      {/* Left: Settings List */}
      <div data-name="settings-sidebar" className="h-full overflow-auto border-r flex flex-col">
        <div data-name="settings-header" className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">Settings</h2>
        </div>
        <div data-name="settings-list" className="flex-1 overflow-auto p-4 space-y-4">
          <ListContainer>
            {myUsername ? (
              <ListItem
                icon={<UserProfilePicture user={myUser} />}
                title={myHandle || myUsername}
                subtitle="Profile"
                showChevron
                onClick={() => setSelectedPanel("profile")}
                className={selectedPanel === "profile" ? "bg-accent" : ""}
              />
            ) : apiToken ? (
              <ListItem
                icon={<User className="w-5 h-5" />}
                title="Profile"
                showChevron
                onClick={() => setSelectedPanel("profile")}
                className={selectedPanel === "profile" ? "bg-accent" : ""}
              />
            ) : null}

            <ListItem
              icon={<Bell className="w-5 h-5" />}
              title="Notifications"
              showChevron
              onClick={() => setSelectedPanel("notifications")}
              className={selectedPanel === "notifications" ? "bg-accent" : ""}
            />

            {apiToken && (
              <ListItem
                icon={<Link2 className="w-5 h-5" />}
                title="Linked Devices"
                subtitle="Manage devices and start web linking"
                showChevron
                onClick={() => navigate("/linked-devices")}
              />
            )}

            {appConfig.appId === "kosmos" &&
              apiToken &&
              hilo.environment !== "hi" && (
                <ListItem
                  icon={<Lock className="w-5 h-5" />}
                  title="Manage Lock Codes"
                  showChevron
                  onClick={() => setSelectedPanel("lock-codes")}
                  className={selectedPanel === "lock-codes" ? "bg-accent" : ""}
                />
              )}

            {myUsername && (
              <ListItem
                icon={<Shield className="w-5 h-5" />}
                title="Account Backup"
                showChevron
                onClick={() => setSelectedPanel("backup")}
                className={selectedPanel === "backup" ? "bg-accent" : ""}
              />
            )}

            <ListItem
              icon={<XCircle className="w-5 h-5 text-red-500" />}
              title="Disconnect"
              onClick={() => disconnectWebSocket()}
              className="text-red-500"
            />

            {apiToken && (
              <ListItem
                icon={<LogOut className="w-5 h-5 text-red-500" />}
                title="Logout"
                onClick={handleLogout}
                className="text-red-500"
              />
            )}
          </ListContainer>

          {/* Version Section */}
          <div data-name="version-section" className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground px-1">
              Version
            </h3>
            <div data-name="version-info" className="bg-card rounded-lg p-4 space-y-2">
              <span>{appVersion}</span>
              <div className="text-xs text-muted-foreground">
                Build: {getBuildDate()}
              </div>
              <div className="text-xs text-muted-foreground">
                Commit: {getGitHash()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Panel */}
      <div data-name="settings-panel" className="h-full">{renderPanel()}</div>
    </div>
  );
};

export default SettingsNav;
